import type {
  AddOrUpdateDefaultMessageCommandDto,
  DefaultMessageType,
  GetCompanyFromPortalResult,
  GetCompanyPlanResult,
  ListOtherEmployersForCompanyEmployer,
  UpdateCompanyFromPortalCommandDto,
} from '@swipe4work/models-orval'
import { useAuthStore } from './auth-store'
import { CompanyPlanChannelMessage, useBroadcastStore } from '@/stores/broadcast-store'
import { listEmployersForCompany, getMyCompany, getMyCompanyPlan, deleteMyCompany, updateMyCompany, addOrUpdateDefaultMessage, deleteDefaultMessage } from '@swipe4work/api-orval'

export const useCompanyStore = defineStore('company', {
  state: () => ({
    company: undefined as GetCompanyFromPortalResult | undefined,
    companyPlan: undefined as GetCompanyPlanResult | undefined,
    employers: [] as ListOtherEmployersForCompanyEmployer[],
    broadcastStore: useBroadcastStore(),
  }),
  getters: {
    canScout: (state) => {
      if (!state.companyPlan) {
        return false
      }

      const authStore = useAuthStore()
      if (!authStore.hasWritePermission('Scout')) {
        return false
      }

      return state.companyPlan.scoutCredits > 0
    },
    canMatch: (state) => {
      if (!state.companyPlan) {
        return false
      }

      return state.companyPlan.canMatch
    },
    matchCredits: state => state.companyPlan?.matchCredits ?? 0,
    companyComplete: (state) => {
      return state.company?.logo != null && state.company?.cover != null && state.company?.aboutUs !== ''
    },
    defaultMessages: state => (type: DefaultMessageType) => {
      return state.company?.defaultMessages.filter(m => m.messageType === type) || []
    },
    allJobsSlotsFilled: (state) => {
      return state.companyPlan?.hasSubscription && state.companyPlan.activeJobs >= (state.companyPlan.jobSlots ?? 0)
    },
    /**
     * The subscription start date while it still lies in the future (backend only fills it then).
     * Until this date publishing jobs is blocked; drafts remain fully usable.
     */
    subscriptionStartsOn: (state) => {
      if (!state.companyPlan?.subscriptionStartsOn) {
        return null
      }

      return new Date(state.companyPlan.subscriptionStartsOn)
    },
    mainAtsIntegrationId: (state) => {
      return state.companyPlan?.mainAtsIntegrationConnectionId
    },
  },
  actions: {
    async fill(): Promise<void> {
      try {
        const promise = [
          this.fetchCompany(),
          this.fetchCompanyPlan(),
          this.fetchEmployersInCompany(),
        ]

        await Promise.all(promise)
      } catch (error) {
        console.error(error)
      }
    },
    async getOrFetchCompany(): Promise<GetCompanyFromPortalResult | undefined> {
      if (!this.company) {
        return this.fetchCompany()
      }

      return this.company
    },
    async fetchEmployersInCompany() {
      const [ err, res ] = await safeResolve(listEmployersForCompany())
      if (err) {
        return
      }

      this.employers = res.employers
    },
    async fetchCompany(): Promise<GetCompanyFromPortalResult | undefined> {
      const [ err, res ] = await safeResolve(getMyCompany())
      if (err) {
        return undefined
      }

      return this.company = res
    },
    async fetchCompanyPlan(fromBroadcastChannel: boolean = false): Promise<GetCompanyPlanResult | undefined> {
      if (!fromBroadcastChannel) {
        this.broadcastStore.postCompanyPlanChannelMessage(CompanyPlanChannelMessage.FetchPlan)
      }
      const [ err, res ] = await safeResolve(getMyCompanyPlan())
      if (err) {
        return undefined
      }

      return this.companyPlan = res
    },
    async deleteCompany(): Promise<void> {
      await safeResolve(deleteMyCompany())
    },
    async updateCompany(company: UpdateCompanyFromPortalCommandDto): Promise<boolean> {
      const [ err ] = await safeResolve(updateMyCompany(company))

      return !err
    },
    /* Add or update a default message. If the ID is provided, the default message is updated, else it is added */
    async addOrUpdateDefaultMessage(defaultMessage: AddOrUpdateDefaultMessageCommandDto) {
      const [ err ] = await safeResolve(addOrUpdateDefaultMessage(defaultMessage))

      return !err
    },
    async deleteDefaultMessage(defaultMessageId: number) {
      await safeResolve(deleteDefaultMessage(defaultMessageId))
    },
  },
})
